import { traverse } from '@atlaskit/adf-utils/traverse/main';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

type NestingNode = {
	type?: string;
	attrs?: {
		extensionKey?: string;
		extensionType?: string;
		parameters?: {
			nestedContent?: unknown;
		};
	};
};

const LCM_EXTENSION_KEY = 'legacy-content';
const LCM_EXTENSION_TYPE = 'com.atlassian.confluence.migration';
const EXTENSION_NODE_TYPES = new Set([
	'extension',
	'inlineExtension',
	'bodiedExtension',
	'multiBodiedExtension',
]);

const isExtensionNode = (node: NestingNode): boolean =>
	typeof node.type === 'string' && EXTENSION_NODE_TYPES.has(node.type);

const isLcmNode = (node: NestingNode): boolean =>
	isExtensionNode(node) &&
	node.attrs?.extensionKey === LCM_EXTENSION_KEY &&
	node.attrs?.extensionType === LCM_EXTENSION_TYPE;

const isObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const getValidExtensionNestedContentDocument = (node: NestingNode): ADFEntity | undefined => {
	if (!isExtensionNode(node)) {
		return undefined;
	}

	const nestedContent = node.attrs?.parameters?.nestedContent;
	if (
		!isObject(nestedContent) ||
		nestedContent.type !== 'doc' ||
		!Array.isArray(nestedContent.content)
	) {
		return undefined;
	}

	return nestedContent as ADFEntity;
};

/**
 * Preserve the existing traversal used by ordinary ADF statistics. Renderer-level telemetry is
 * intentionally stricter and only advances for getValidExtensionNestedContentDocument().
 */
const getNestedContentForTraversal = (node: NestingNode): ADFEntity | undefined => {
	const nestedContent = node.attrs?.parameters?.nestedContent;
	return isObject(nestedContent) && Array.isArray(nestedContent.content)
		? (nestedContent as ADFEntity)
		: undefined;
};

/**
 * Tracks renderer nesting created by valid extension nestedContent documents. The outer exact LCM
 * document is level zero; ordinary ADF and extensions without nestedContent are depth-neutral.
 */
export function createLcmNestingLevelTracker(): {
	getMaxNestingLevel: () => number;
	visit: (lcmNestedContentLevel: number | undefined) => void;
} {
	let maxNestingLevel = 0;

	return {
		getMaxNestingLevel: () => maxNestingLevel,
		visit: (lcmNestedContentLevel) => {
			if (lcmNestedContentLevel !== undefined) {
				maxNestingLevel = Math.max(maxNestingLevel, lcmNestedContentLevel);
			}
		},
	};
}

/**
 * Extends traverse from @atlaskit/adf-utils to also walk into nestedContent
 * found in extension parameters (e.g. legacy-content, column macro).
 *  "content": [
        {
          "type": "extension",
          "attrs": {
            "extensionKey": "legacy-content",
            "extensionType": "com.atlassian.confluence.migration",
            "parameters": {
              "reason": "A column macro in a rich body macro can't be created or edited in the new editor.",
              "nestedContent": {
                "type": "doc",
                "content": [
                  {
                    "type": "bodiedExtension",
                    "attrs": {
                      "extensionType": "com.atlassian.confluence.macro.core",
                      "extensionKey": "column"
                  }
                ]
              }
            }
          }
        }
      ]
    }
  }
]
 */
export function traverseWithNestedContentAndDepth(
	adf: ADFEntity,
	visitor: (node: ADFEntity, depth: number, lcmNestedContentLevel: number | undefined) => void,
	depthOffset: number = 0,
): void {
	const activeDocuments = new WeakSet<object>();
	const walk = (
		document: ADFEntity,
		currentDepthOffset: number,
		lcmNestedContentLevel: number | undefined,
		isLcmTrackingEnabled: boolean,
	): void => {
		if (activeDocuments.has(document)) {
			return;
		}

		activeDocuments.add(document);
		try {
			traverse(document, {
				any: (node, _parent, _index, depth) => {
					visitor(node, currentDepthOffset + depth, lcmNestedContentLevel);

					const validNestedContent = getValidExtensionNestedContentDocument(node);
					const nestedContent = validNestedContent ?? getNestedContentForTraversal(node);
					if (nestedContent) {
						const childLcmNestedContentLevel =
							validNestedContent && isLcmTrackingEnabled
								? lcmNestedContentLevel !== undefined
									? lcmNestedContentLevel + 1
									: isLcmNode(node)
										? 0
										: undefined
								: undefined;

						walk(
							nestedContent,
							currentDepthOffset + depth + 1,
							childLcmNestedContentLevel,
							isLcmTrackingEnabled && Boolean(validNestedContent),
						);
					}

					return node;
				},
			});
		} finally {
			activeDocuments.delete(document);
		}
	};

	walk(adf, depthOffset, undefined, true);
}
